import { computed, readonly, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { useToasts } from '@/composables/useToasts'
import { useLibraryStore } from '@/pages/infoLibrary/stores/LibraryStore'
import { useCommonStore } from '@/stores/CommonStore'
import type { Card } from '@/pages/infoLibrary/config/library.types'

import {
  getTabs,
  getAllCards
} from '@/pages/infoLibrary/services/library.repository'

import { useLibraryEntry } from './useLibraryEntry'
import { convertReadonlyCardToRaw } from '../config/library.utils'

/* =========================================================
   Composable
========================================================= */

const isLoading = ref(false)
const isStale = ref(false)
const isRefreshing = ref(false)
const hasFreshData = ref(false)
const notificationAffectedCardId = ref<number | null>(null)

export function useLibraryList() {
  const { t } = useI18n()

  const { clearEntry } = useLibraryEntry()
  const { showErrorToast } = useToasts()

  const common = useCommonStore()
  const { fullUserInfo } = storeToRefs(common)

  const libraryStore = useLibraryStore()
  const { tabs, cards, tabCards, selectedTabId, selectedCard, selectedCardId } =
    storeToRefs(libraryStore)
  const { setCards, setSelectedCard, setTabs, setIsFetchingCards } =
    libraryStore

  function resetLibraryListSharedState(): void {
    isLoading.value = false
    isStale.value = false
    isRefreshing.value = false
    hasFreshData.value = false
    notificationAffectedCardId.value = null
    setIsFetchingCards(false)
  }

  const rawSelectedCard = computed(() => {
    if (!selectedCard.value) return null

    return convertReadonlyCardToRaw(selectedCard.value)
  })

  const rawTabCards = computed(() =>
    tabCards.value.map((card) => convertReadonlyCardToRaw(card))
  )

  function showLoadCardsErrorToast(error: unknown): void {
    const errorMessage =
      error instanceof Error && error.message === 'library_offline_no_cache'
        ? t('infoLibrary.errors.loadCardsOffline')
        : t('infoLibrary.errors.loadCards')

    showErrorToast(errorMessage)
  }

  function getIsSharedToMe(card: Card): boolean {
    return fullUserInfo.value.userId !== card.userId
  }

  function getIsSharedCardNewOrUpdated(card: Card): boolean {
    return (
      getIsSharedToMe(card) &&
      (!card.viewedWhen ||
        (card.updatedWhen != null && card.updatedWhen > card.viewedWhen))
    )
  }

  function reconcileSelectedCard(affectedCardId?: number): void {
    const selectedId = selectedCardId.value

    if (selectedId == null) return

    const nextCard =
      cards.value.find((card) => card.itemId === selectedId) ?? null

    if (!nextCard) {
      clearEntry()

      return
    }

    const nextSelectedCard = convertReadonlyCardToRaw(nextCard)

    if (
      getIsSharedCardNewOrUpdated(nextSelectedCard) &&
      (affectedCardId == null || affectedCardId === nextSelectedCard.itemId)
    ) {
      clearEntry()
      return
    }

    setSelectedCard(nextSelectedCard)
  }

  /* =========================================================
     LOAD TABS
  ========================================================= */
  async function loadTabs(): Promise<void> {
    try {
      const result = await getTabs()

      setTabs(result.data)
    } catch (error: unknown) {
      console.error('Error loading tabs:', error)
    }
  }

  /* =========================================================
     LOAD CARDS
  ========================================================= */
  async function loadCards(fetchAll?: boolean): Promise<void> {
    isLoading.value = true

    try {
      if (fetchAll) {
        const result = await getAllCards()

        notificationAffectedCardId.value = null
        setCards(result.data)
        isStale.value = result.isStale

        reconcileSelectedCard()

        if (result.isStale) {
          refreshInBackground()
        }

        return
      }

      notificationAffectedCardId.value = null
      reconcileSelectedCard()
    } catch (error: unknown) {
      if (cards.value.length === 0) {
        showLoadCardsErrorToast(error)
      }
    } finally {
      isLoading.value = false
    }
  }

  /* =========================================================
     BACKGROUND REFRESH
  ========================================================= */
  async function refreshInBackground(): Promise<void> {
    isRefreshing.value = true
    let refreshSucceeded = false

    try {
      const fresh = await getAllCards(undefined, { forceRefresh: true })

      notificationAffectedCardId.value = null
      setCards(fresh.data)
      reconcileSelectedCard()
      refreshSucceeded = true
    } catch (error: unknown) {
      console.warn('[useLibraryList] refreshInBackground error:', error)
    } finally {
      isRefreshing.value = false

      if (refreshSucceeded) {
        isStale.value = false
        hasFreshData.value = true
      }
    }
  }

  /* =========================================================
     RELOAD (forces full refresh)
  ========================================================= */
  async function reload(): Promise<void> {
    await loadTabs()
    await loadCards(true)
  }

  /* =========================================================
     NOTIFICATION HANDLER
  ========================================================= */
  async function handleLibraryNotifications(itemId?: number): Promise<void> {
    const cardsResult = await getAllCards(undefined, { forceRefresh: true })

    notificationAffectedCardId.value = itemId ?? null
    setCards(cardsResult.data)
    reconcileSelectedCard(itemId)

    common.fetchBadgeCounts()
  }

  return {
    tabs,
    cards,
    tabCards,
    selectedTabId,

    isLoading,
    isStale,
    isRefreshing,
    hasFreshData,

    resetLibraryListSharedState,

    rawSelectedCard,
    rawTabCards,
    notificationAffectedCardId: readonly(notificationAffectedCardId),

    loadTabs,
    loadCards,
    reload,
    handleLibraryNotifications
  }
}
